import React from 'react';

const CASE_STUDIES = [
  {
    num: '01',
    category: 'Service Site',
    title: 'Local Service Business Redesign',
    description:
      'A redesigned website for a local service business: Focused on clear navigation, trust-building content, and a seamless booking flow. Applying user-centered design and heuristic evaluation principles.',
    icon: '✂️',
    tags: ['UI Design', 'Figma', 'React'],
    gradientClass: 'thumb-service',
  },
  {
    num: '02',
    category: 'Jeu de Mémoire',
    title: 'Memory Card Game',
    description:
      'An interactive memory game (jeu de mémoire): A playful, accessible UI with smooth animations, score tracking, and engaging micro-interactions to make the experience delightful.',
    icon: '🃏',
    tags: ['Game Design', 'JavaScript', 'CSS Animations'],
    gradientClass: 'thumb-memory',
  },
  {
    num: '03',
    category: 'E-Commerce',
    title: 'Online Shop Experience',
    description:
      'A full e-commerce site design: Product browsing, cart management, and a streamlined checkout flow with accessibility at its core. Built with real-world conversion principles.',
    icon: '🛒',
    tags: ['E-Commerce', 'UX Design', 'React'],
    gradientClass: 'thumb-ecommerce',
  },
  {
    num: '04',
    category: 'Analytics Dashboard',
    title: 'Data Visualization Dashboard',
    description:
      'An analytics/BI dashboard with rich data visualizations: Designed to make complex information immediately readable and actionable. Inspired by sports analytics and business intelligence.',
    icon: '📊',
    tags: ['Data Viz', 'Dashboard', 'BI'],
    gradientClass: 'thumb-analytics',
  },
];

const thumbStyles = {
  'thumb-service':   'linear-gradient(135deg, #0a1628 0%, #0d2a52 50%, #1a3a70 100%)',
  'thumb-memory':    'linear-gradient(135deg, #120a28 0%, #2a1052 50%, #3a1a70 100%)',
  'thumb-ecommerce': 'linear-gradient(135deg, #0a1a28 0%, #0d3a52 50%, #1a5070 100%)',
  'thumb-analytics': 'linear-gradient(135deg, #0a2814 0%, #0d521a 50%, #1a7030 100%)',
};

function CaseCard({ num, category, title, description, icon, tags, gradientClass }) {
  return (
    <div
      className="dark-card d-flex flex-column"
      style={{ transition: 'border-color 0.25s, transform 0.25s', overflow: 'hidden' }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'rgba(77,148,255,0.5)';
        e.currentTarget.style.transform = 'translateY(-6px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: '100%', height: 200,
          background: thumbStyles[gradientClass],
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
        }}
      >
        <span style={{ fontSize: '3.2rem', opacity: 0.5 }}>{icon}</span>
        <span
          style={{
            position: 'absolute', top: '1rem', right: '1rem',
            background: 'rgba(26,108,245,0.2)',
            border: '1px solid rgba(26,108,245,0.4)',
            color: 'var(--blue-light)',
            fontSize: '0.7rem', fontWeight: 600,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            padding: '0.28rem 0.75rem', borderRadius: 4,
          }}
        >
          Coming Soon
        </span>
      </div>

      {/* Body */}
      <div className="p-4 flex-grow-1 d-flex flex-column">
        <div style={{ color: 'var(--blue-light)', fontSize: '0.73rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>
          Design {num} · {category}
        </div>
        <h4 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: '1.08rem', color: 'var(--text)', marginBottom: '0.6rem' }}>
          {title}
        </h4>
        <p style={{ color: 'var(--muted)', fontSize: '0.87rem', lineHeight: 1.65, flexGrow: 1, marginBottom: '1rem' }}>
          {description}
        </p>

        {/* Tags — Bootstrap badges */}
        <div className="d-flex flex-wrap gap-2">
          {tags.map(tag => (
            <span
              key={tag}
              className="badge"
              style={{
                background: 'rgba(26,108,245,0.12)',
                border: '1px solid rgba(26,108,245,0.22)',
                color: 'var(--blue-light)',
                fontWeight: 500, fontSize: '0.75rem', padding: '0.3rem 0.7rem',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '0.9rem 1.5rem',
          borderTop: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '0.82rem', color: 'var(--muted)',
        }}
      >
        <span>🔒 Hold page — design in progress</span>
        <span style={{ color: 'var(--blue-light)', fontSize: '1.1rem' }}>→</span>
      </div>
    </div>
  );
}

function CaseStudies() {
  return (
    <section id="caseStudies" className="section-wrapper" style={{ maxWidth: 1200 }}>
      <p className="section-label">03. My Work</p>
      <h2 className="section-title">
        Case <span className="gradient-word">Studies</span>
      </h2>

      {/* Bootstrap 2-col grid */}
      <div className="row g-4">
        {CASE_STUDIES.map(cs => (
          <div className="col-md-6" key={cs.num}>
            <CaseCard {...cs} />
          </div>
        ))}
      </div>

      {/* Progress note */}
      <div
        className="text-center mt-5"
        style={{ color: 'var(--muted)', fontSize: '0.9rem' }}
      >
        <span style={{ color: 'var(--blue-light)' }}>✦</span> Case studies will be published throughout the semester as each design is completed.
      </div>
    </section>
  );
}

export default CaseStudies;
